import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Search, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import BlogCard from '../components/common/BlogCard';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const { slug } = useParams();
  const [blogs, setBlogs] = useState([]);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const blogsPerPage = 9;

  const categories = ['All', 'GST', 'Startup Services', 'Registration', 'Compliances', 'Legal'];

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const [blogsRes, countRes] = await Promise.all([
        axios.get(`${API}/blogs`, {
          params: { page: currentPage, limit: blogsPerPage, category: selectedCategory }
        }),
        axios.get(`${API}/blogs/count`, {
          params: { category: selectedCategory }
        })
      ]);
      setBlogs(blogsRes.data);
      setTotalBlogs(countRes.data.total);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
    setLoading(false);
  }, [currentPage, selectedCategory]);

  const fetchBlogBySlug = useCallback(async (blogSlug) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blogs/${blogSlug}`);
      setCurrentBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (slug) {
      fetchBlogBySlug(slug);
    } else {
      fetchBlogs();
    }
  }, [slug, fetchBlogs, fetchBlogBySlug]);

  const totalPages = Math.ceil(totalBlogs / blogsPerPage);

  const filteredBlogs = blogs.filter(blog => 
    blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Single Blog View
  if (slug && currentBlog) {
    return (
      <div className="min-h-screen pt-20" data-testid="blog-detail-page">
        <Helmet>
          <title>{currentBlog.title} - GK Legal Solutions Blog</title>
          <meta name="description" content={currentBlog.excerpt} />
          <link rel="canonical" href={`https://gklegalsolutions.com/blog/${currentBlog.slug}`} />
        </Helmet>
        <article className="section-spacing">
          <div className="container-custom">
            <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 font-body text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <header className="mb-8">
                <Badge className="bg-primary text-white mb-4">{currentBlog.category}</Badge>
                <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                  {currentBlog.title}
                </h1>
                <div className="flex items-center gap-4 text-slate-600 font-body text-sm">
                  <span>By {currentBlog.author}</span>
                  <span>•</span>
                  <span>{currentBlog.read_time} read</span>
                  <span>•</span>
                  <span>{currentBlog.published_at}</span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="rounded-xl overflow-hidden mb-10">
                <img 
                  src={currentBlog.image}
                  alt={currentBlog.title}
                  className="w-full h-[400px] object-cover"
                />
              </div>

              {/* Content */}
              <div 
                className="prose prose-lg max-w-none font-body text-slate-700"
                dangerouslySetInnerHTML={{ __html: currentBlog.content }}
              />

              {/* CTA */}
              <div className="mt-12 p-8 bg-primary/5 rounded-xl border border-primary/20">
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                  Need Help with {currentBlog.category}?
                </h3>
                <p className="font-body text-slate-600 mb-6">
                  Our expert team is ready to assist you. Get a free consultation today!
                </p>
                <Link to="/contact">
                  <Button className="btn-primary" data-testid="blog-cta-btn">
                    Contact Us Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>
      </div>
    );
  }

  // Blog Listing
  return (
    <div className="min-h-screen pt-20" data-testid="blog-page">
      <Helmet>
        <title>Blog - GK Legal Solutions | Business Registration & Compliance Insights</title>
        <meta name="description" content="Read expert articles on Company Registration, GST, Trademark, FSSAI, Income Tax Filing, and Business Compliance. Stay updated with latest legal insights." />
        <link rel="canonical" href="https://gklegalsolutions.com/blog" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Our Blog</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-4">
              Insights & Resources
            </h1>
            <p className="font-body text-lg text-slate-600">
              Stay updated with the latest in business registration, GST, compliance, and legal matters.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-full font-body text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`blog-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="blog-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="font-body text-slate-600 mt-4">Loading articles...</p>
            </div>
          ) : filteredBlogs.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredBlogs.map((blog) => (
                  <BlogCard key={blog.id} blog={blog} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    data-testid="blog-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        className={currentPage === pageNum ? 'bg-primary' : ''}
                        onClick={() => setCurrentPage(pageNum)}
                        data-testid={`blog-page-${pageNum}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  {totalPages > 5 && <span className="px-2">...</span>}
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    data-testid="blog-next-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <p className="text-center font-body text-sm text-slate-500 mt-6">
                Showing {filteredBlogs.length} of {totalBlogs} articles
              </p>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="font-heading text-2xl text-slate-700 mb-2">No articles found</p>
              <p className="font-body text-slate-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
